import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import {
  Lightbulb, MessageSquare, Share2, ChevronUp, RefreshCw, Copy, Mail,
  Sparkles, Calendar, BarChart3, Send, ExternalLink, AlertTriangle, ArrowUpRight, ArrowDownRight, ArrowRight,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip as ReTooltip, YAxis } from "recharts";

const KIND_TONE = {
  idea: "bg-amber-100 text-amber-700 border-amber-200",
  feedback: "bg-blue-100 text-blue-700 border-blue-200",
  share: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

/**
 * Admin "Voice of the Network" digest — top-voted ideas + most-discussed
 * feedback + recent shares for monthly community recap emails.
 */
export default function AdminThinkTankDigest() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/think-tank/digest")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const copyEmailMarkdown = async () => {
    if (!data) return;
    const md = renderEmailMarkdown(data);
    try {
      await navigator.clipboard.writeText(md);
      toast.success("Email-ready markdown copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const openMail = () => {
    if (!data) return;
    const subject = `Voice of the Network · ${data.month}`;
    const body = renderEmailMarkdown(data);
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Think Tank"
      title="Voice of the Network · Monthly digest"
      intro="The top-voted ideas, most-discussed feedback, and freshest shares from the Think Tank — auto-aggregated for your monthly community recap email."
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            data-testid="digest-refresh"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-50"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
          <button
            type="button"
            onClick={copyEmailMarkdown}
            data-testid="digest-copy"
            disabled={!data}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-800 disabled:opacity-50"
          >
            <Copy className="w-3 h-3" /> Copy email-ready markdown
          </button>
          <button
            type="button"
            onClick={openMail}
            data-testid="digest-mailto"
            disabled={!data}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50"
          >
            <Send className="w-3 h-3" /> Open in mail
          </button>
        </div>
      }
    >
      {loading || !data ? (
        <div className="text-sm text-slate-500">Loading digest…</div>
      ) : (
        <div className="space-y-5" data-testid="digest-content">
          {data.network_vitals && <NetworkVitalsHeader vitals={data.network_vitals} />}

          {/* Period + totals */}
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white p-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-blue-200 inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> {data.month}
            </div>
            <h2 className="font-display text-2xl font-extrabold mt-1">Voice of the Network</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Stat label="Total posts" value={data.totals.total_posts} />
              <Stat label="Ideas" value={data.totals.ideas_total} />
              <Stat label="Feedback" value={data.totals.feedback_total} />
              <Stat label="Shares" value={data.totals.shares_total} />
              <Stat label="New this month" value={data.totals.new_this_month} accent="text-emerald-300" />
            </div>
          </div>

          <DigestSection
            kind="idea"
            title="Top ideas"
            subtitle="Ranked by upvote count"
            metric="upvote"
            icon={Lightbulb}
            posts={data.top_ideas}
          />
          <DigestSection
            kind="feedback"
            title="Most discussed feedback"
            subtitle="Ranked by comment count"
            metric="comment"
            icon={MessageSquare}
            posts={data.top_feedback}
          />
          <DigestSection
            kind="share"
            title="Recent shares from the network"
            subtitle="Most recent first"
            metric="upvote"
            icon={Share2}
            posts={data.new_shares}
          />

          {data.off_platform_metric && <OffPlatformPanel metric={data.off_platform_metric} />}

          <div className="text-[10px] text-slate-400 text-center pt-2">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        </div>
      )}
    </ToolPageShell>
  );
}

function Stat({ label, value, accent = "text-white" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-200/80">{label}</div>
      <div className={`font-display text-2xl font-extrabold tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function DigestSection({ kind, title, subtitle, metric, icon: Icon, posts }) {
  return (
    <section data-testid={`digest-section-${kind}`} className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 inline-flex items-center gap-1.5">
            <Icon className="w-3 h-3" /> {title}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>
        </div>
        <span className="text-[10px] text-slate-400">{posts.length} item{posts.length === 1 ? "" : "s"}</span>
      </div>
      {posts.length === 0 ? (
        <div className="text-xs text-slate-500 italic">No items yet — encourage the network to post.</div>
      ) : (
        <ul className="space-y-2">
          {posts.map((p, i) => (
            <li key={p.id} data-testid={`digest-${kind}-${i}`} className="rounded-xl bg-slate-50 border border-slate-100 p-3 flex items-start gap-3">
              <span className="w-7 h-7 rounded-md bg-white border border-slate-200 text-slate-500 text-[11px] font-bold flex items-center justify-center shrink-0">
                #{i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full border px-2 py-0.5 ${KIND_TONE[p.kind] || "bg-slate-100 text-slate-700"}`}>
                    {p.kind}
                  </span>
                  {p.status && p.status !== "open" && (
                    <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-slate-200 text-slate-700">
                      {String(p.status).replace("_", " ")}
                    </span>
                  )}
                  <span className="font-semibold text-slate-900 text-sm">{p.title}</span>
                </div>
                <p className="text-xs text-slate-700 line-clamp-2 mt-0.5">{p.body}</p>
                <div className="text-[10px] text-slate-500 mt-1">{p.author_name || "Anonymous"}{p.author_clinic ? ` · ${p.author_clinic}` : ""}</div>
              </div>
              <div className="text-right shrink-0 text-xs text-slate-700">
                {metric === "upvote" ? (
                  <span className="inline-flex items-center gap-1 font-bold">
                    <ChevronUp className="w-3 h-3 text-amber-700" /> {p.upvotes || 0}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-bold">
                    <MessageSquare className="w-3 h-3 text-blue-700" /> {p.comment_count || 0}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Plain-text email body — copy/paste-ready markdown.
function renderEmailMarkdown(d) {
  const lines = [];
  lines.push(`# Voice of the Network · ${d.month}`);
  lines.push("");
  lines.push(`**Highlights from the Nodera Think Tank.** ${d.totals.new_this_month} new post${d.totals.new_this_month === 1 ? "" : "s"} this month — ${d.totals.shipped_this_month} idea${d.totals.shipped_this_month === 1 ? "" : "s"} shipped.`);
  lines.push("");
  if (d.network_vitals) {
    const v = d.network_vitals;
    lines.push(`## Network vitals · ${v.window?.start_label} → ${v.window?.end_label}`);
    const fmt = (t, invert) => {
      if (!t) return "";
      const cur =
        t.unit === "$"
          ? `$${Number(t.current || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
          : t.unit === "%"
          ? `${t.current}%`
          : Number(t.current || 0).toLocaleString();
      let delta = "";
      if (t.delta != null && t.delta !== 0) {
        const sign = t.delta > 0 ? "+" : "";
        const d_ =
          t.unit === "$"
            ? `$${Math.abs(t.delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
            : t.unit === "%"
            ? `${t.delta}pp`
            : `${t.delta}`;
        const goodUp = !invert;
        const isGood = (goodUp && t.delta > 0) || (!goodUp && t.delta < 0);
        delta = ` (${sign}${d_} MoM, ${isGood ? "↑ good" : "↓ watch"})`;
      }
      return `- **${t.label}**: ${cur}${delta}`;
    };
    lines.push(fmt(v.off_platform_rate, true));
    lines.push(fmt(v.revenue_paid, false));
    lines.push(fmt(v.throughput_completed, false));
    lines.push("");
  }
  const sec = (title, items, kind) => {
    if (!items.length) return;
    lines.push(`## ${title}`);
    lines.push("");
    items.forEach((p, i) => {
      const metric = kind === "feedback" ? `${p.comment_count || 0} comments` : `${p.upvotes || 0} upvotes`;
      lines.push(`${i + 1}. **${p.title}** _(${metric})_`);
      lines.push(`   ${(p.body || "").slice(0, 240)}`);
      lines.push(`   — ${p.author_name || "Anonymous"}${p.author_clinic ? ` · ${p.author_clinic}` : ""}`);
      lines.push("");
    });
  };
  sec("Top ideas", d.top_ideas, "idea");
  sec("Most discussed feedback", d.top_feedback, "feedback");
  sec("Recent shares", d.new_shares, "share");
  if (d.off_platform_metric) {
    const m = d.off_platform_metric;
    lines.push(`## Network curation · off-platform fulfilment`);
    lines.push("");
    const trendStr = m.trend
      ? ` (${m.trend.delta_pp >= 0 ? "+" : ""}${m.trend.delta_pp}pp from ${m.trend.previous_month})`
      : "";
    lines.push(
      `${m.off_platform_count} of ${m.approved_total} approved vendor services route clinics off-platform — **${m.off_platform_rate_pct}%**${trendStr}. Watch this number; rising means the network is over-relying on external systems.`,
    );
    if (m.examples?.length) {
      lines.push("");
      m.examples.forEach((s) => {
        lines.push(`- ${s.name}${s.vendor_name ? ` · ${s.vendor_name}` : ""}${s.category ? ` (${s.category})` : ""}`);
      });
    }
    lines.push("");
  }
  lines.push("");
  lines.push("Reply to this email or post in Think Tank to keep the conversation going.");
  return lines.join("\n");
}

function OffPlatformPanel({ metric }) {
  const { approved_total, off_platform_count, off_platform_rate_pct, vendor_breakdown, examples, trend, history } = metric;
  const empty = !approved_total;
  const tone =
    off_platform_rate_pct >= 30
      ? { wrap: "border-rose-200 bg-rose-50", pill: "bg-rose-200 text-rose-900", text: "text-rose-900" }
      : off_platform_rate_pct >= 10
      ? { wrap: "border-amber-200 bg-amber-50", pill: "bg-amber-200 text-amber-900", text: "text-amber-900" }
      : { wrap: "border-emerald-200 bg-emerald-50", pill: "bg-emerald-200 text-emerald-900", text: "text-emerald-900" };

  // Trend arrow — direction-coloured pill that hugs the rate pill so admins
  // see month-over-month drift at a glance.
  const trendNode = trend ? (() => {
    const Icon = trend.direction === "up" ? ArrowUpRight : trend.direction === "down" ? ArrowDownRight : ArrowRight;
    const tcls =
      trend.direction === "up"
        ? "bg-rose-100 text-rose-700 border-rose-200"
        : trend.direction === "down"
        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
        : "bg-slate-100 text-slate-600 border-slate-200";
    const sign = trend.delta_pp > 0 ? "+" : "";
    return (
      <span
        data-testid="digest-off-platform-trend"
        title={`${trend.previous_rate_pct}% in ${trend.previous_month} → ${off_platform_rate_pct}% now`}
        className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold rounded-full border px-2 py-0.5 ${tcls}`}
      >
        <Icon className="w-3 h-3" />
        {sign}{trend.delta_pp}pp from {trend.previous_month}
      </span>
    );
  })() : (
    <span className="text-[10px] uppercase font-semibold text-slate-500" data-testid="digest-off-platform-trend-empty">
      First snapshot — trend appears next month
    </span>
  );

  return (
    <section
      data-testid="digest-off-platform-panel"
      className={`rounded-2xl border ${tone.wrap} p-5`}
    >
      <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
        <div className="min-w-0">
          <div className={`text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1.5 ${tone.text}`}>
            <ExternalLink className="w-3 h-3" /> Network curation · off-platform fulfilment
          </div>
          <p className="text-[11px] text-slate-600 mt-0.5">
            % of approved vendor services that route clinics outside Nodera. Rising means the
            network is over-relying on external systems — re-curate when this number drifts.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${tone.pill}`}>
            {empty ? "no services" : `${off_platform_rate_pct}%`}
          </span>
          {trendNode}
        </div>
      </div>
      {history && history.length >= 2 && (
        <div
          className="mb-4 rounded-xl bg-white/60 border border-slate-200 px-3 pt-2 pb-1"
          data-testid="digest-off-platform-sparkline"
        >
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
              Snapshot history
            </div>
            <div className="text-[10px] text-slate-500">
              {history.length} month{history.length === 1 ? "" : "s"} · {history[0].month_label} → {history[history.length - 1].month_label}
            </div>
          </div>
          <div className="h-14">
            <ResponsiveContainer>
              <LineChart
                data={history.map((h) => ({
                  month: h.month_label,
                  rate: Number(h.off_platform_rate_pct || 0),
                }))}
                margin={{ top: 6, right: 6, bottom: 0, left: 0 }}
              >
                <YAxis hide domain={[0, 100]} />
                <ReTooltip
                  cursor={{ stroke: "#94a3b8", strokeDasharray: "3 3" }}
                  contentStyle={{
                    fontSize: 11,
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
                  }}
                  labelStyle={{ fontWeight: 700, color: "#0f172a" }}
                  formatter={(v) => [`${v}%`, "off-platform"]}
                />
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="#dc2626"
                  strokeWidth={2}
                  dot={{ r: 2.5, stroke: "#dc2626", strokeWidth: 1, fill: "white" }}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat label="Approved services" value={approved_total} accent="text-slate-900" />
        <Stat label="Off-platform" value={off_platform_count} accent="text-slate-900" />
        <Stat
          label="Off-platform rate"
          value={empty ? "—" : `${off_platform_rate_pct}%`}
          accent="text-slate-900"
        />
      </div>
      {!!vendor_breakdown?.length && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-1.5 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" /> Top vendors using external links
          </div>
          <ul className="space-y-1">
            {vendor_breakdown.map((v) => (
              <li
                key={v.vendor_name}
                data-testid={`digest-off-platform-vendor-${v.vendor_name}`}
                className="text-xs text-slate-700 flex items-center justify-between bg-white/60 border border-slate-200 rounded-lg px-3 py-1.5"
              >
                <span className="font-semibold">{v.vendor_name}</span>
                <span className="text-slate-500">
                  {v.off_platform} / {v.total} services off-platform
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!!examples?.length && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-1.5">
            Examples
          </div>
          <div className="flex flex-wrap gap-1.5">
            {examples.map((s, i) => (
              <span
                key={`${s.name}-${s.vendor_name || ""}-${i}`}
                className="text-[11px] bg-white/70 border border-slate-200 rounded-full px-2.5 py-0.5 text-slate-700"
              >
                {s.name}
                {s.vendor_name && (
                  <span className="text-slate-500 ml-1">· {s.vendor_name}</span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

void Sparkles; void BarChart3; void Mail;

const TONE_STROKE = {
  rose: "#dc2626",
  emerald: "#059669",
  blue: "#2563eb",
};
const TONE_DELTA = {
  // For revenue + throughput, up=good. For off-platform, up=bad.
  good_up: "text-emerald-700 bg-emerald-100 border-emerald-200",
  bad_up: "text-rose-700 bg-rose-100 border-rose-200",
  flat: "text-slate-600 bg-slate-100 border-slate-200",
};

function _fmt(value, unit) {
  if (value == null) return "—";
  if (unit === "$") return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (unit === "%") return `${value}%`;
  return Number(value).toLocaleString();
}

function NetworkVitalsHeader({ vitals }) {
  const w = vitals.window || {};
  return (
    <section
      data-testid="digest-network-vitals"
      className="rounded-2xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-700 inline-flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3" /> Network vitals
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Three matched 12-month sparklines so creep, growth, and throughput are visible at a
            glance.
          </p>
        </div>
        {w.start_label && (
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">
            {w.start_label} → {w.end_label}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <VitalCard track={vitals.off_platform_rate} invertDelta />
        <VitalCard track={vitals.revenue_paid} />
        <VitalCard track={vitals.throughput_completed} />
      </div>
    </section>
  );
}

function VitalCard({ track, invertDelta }) {
  if (!track) return null;
  const stroke = TONE_STROKE[track.tone] || "#475569";
  const dir =
    track.delta == null || track.delta === 0
      ? "flat"
      : track.delta > 0
      ? "up"
      : "down";
  // For off-platform: up = bad (rose), down = good (emerald). For others: opposite.
  let deltaTone = "flat";
  if (dir !== "flat") {
    const goodUp = !invertDelta;
    const isGood = (goodUp && dir === "up") || (!goodUp && dir === "down");
    deltaTone = isGood ? "good_up" : "bad_up";
  }
  const sign = (track.delta || 0) > 0 ? "+" : "";
  return (
    <article
      className="rounded-xl border border-slate-100 bg-slate-50/40 px-3 pt-3 pb-1"
      data-testid={`digest-vital-${track.label}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
          {track.label}
        </span>
        {dir !== "flat" && (
          <span
            className={`inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full border px-1.5 py-0.5 ${
              TONE_DELTA[deltaTone] || TONE_DELTA.flat
            }`}
          >
            {dir === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {sign}
            {track.unit === "$"
              ? `$${Math.abs(track.delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : track.unit === "%"
              ? `${track.delta}pp`
              : `${track.delta}`}
          </span>
        )}
      </div>
      <div className="font-display text-2xl font-extrabold text-slate-900 tabular-nums mt-0.5">
        {_fmt(track.current, track.unit)}
      </div>
      <div className="h-12 mt-1">
        <ResponsiveContainer>
          <LineChart
            data={(track.history || []).map((h) => ({
              month: h.month_label,
              value: h.value == null ? null : Number(h.value),
            }))}
            margin={{ top: 4, right: 2, bottom: 0, left: 0 }}
          >
            <YAxis hide domain={["dataMin", "dataMax"]} />
            <ReTooltip
              cursor={{ stroke: "#94a3b8", strokeDasharray: "3 3" }}
              contentStyle={{
                fontSize: 11,
                borderRadius: 8,
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
              }}
              labelStyle={{ fontWeight: 700, color: "#0f172a" }}
              formatter={(v) => [_fmt(v, track.unit), track.label]}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={stroke}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}
